
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
    reactive,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import IveFilter from '@/components/dropdown/IVEFilter.vue';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';

  export default defineComponent({
    name: 'ive-areas-listing',
    components: {
      Datatable,
      IveFilter,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const formatDate = (date) => {
        return new Date(date).toISOString().slice(0, 10);
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('areasIveValuesListing'), [
          translate('ive'),
        ]);
        reinitializeComponents();
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_IVE_AREAS_STORE);
      });

      await store.dispatch(Actions.GET_IVE_AREAS);
      store.dispatch(Actions.EXPORT_IVE_AREA);
      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('actions'),
          key: 'actions',
        },
        {
          name: translate('idColumn'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('areaNameEn'),
          key: 'titleEn',
          sortable: true,
        },
        {
          name: translate('areaNameAr'),
          key: 'titleAr',
          sortable: true,
        },
        {
          name: translate('updatedAt'),
          key: 'updatedAt',
          sortable: true,
        },
        {
          name: translate('value'),
          key: 'price',
          sortable: true,
        },
        {
          name: translate('isSeaView'),
          key: 'isSeaView',
          sortable: true,
        },
        {
          name: translate('baseSize'),
          key: 'baseSize',
          sortable: true,
        },
        {
          name: translate('basePrice'),
          key: 'basePrice',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'isActive',
          sortable: true,
        },
      ]);
      const excelHeader = reactive({
        'Area arabic title': 'titleAr',
        'Area english title ': 'titleEn',
        'Total price': 'basePrice',
        'Premium size': 'baseSize',
        'Price per meter  ': 'price',
        'created date': {
          field: 'createdAt',
          callback: (value) => {
            return formatDate(value);
          },
        },
        'updated date': {
          field: 'updatedAt',
          callback: (value) => {
            return formatDate(value);
          },
        },
      });

      const toggleStatusIveArea = async (area) => {
        const deletedMessage = area.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_IVE_AREA, area.id);
          await store.dispatch(Actions.GET_IVE_AREAS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = area.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const editArea = (id) => {
        router.push({ name: 'ive-areas-editing', params: { id } });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_IVE_AREA_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_IVE_AREAS);
        loading.value = false;
      };
      const onTypeChange = async (values) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_IVE_AREA_FILTER, values.value);
        await store.dispatch(Actions.UPDATE_IVE_AREA_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_IVE_AREAS);
        loading.value = false;
      };
      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_IVE_AREA_FILTER, value);
        await store.dispatch(Actions.GET_IVE_AREAS);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        totalItems: computed(() => store.getters.iveAreasListCount),
        tableData: computed(() => store.getters.iveAreasList),
        excelData: computed(() => store.getters.exportedIveAreasList),
        tableHeader,
        excelHeader,
        toggleStatusIveArea,
        translate,
        pageChanged,
        onTypeChange,
        onReset,
        goTo,
        editArea,
        loading,
        can,
      };
    },
  });
